import React from "react";

import Button from "@ingka/button";
import { ListBoxGroup, MenuItem } from "@ingka/list-box";
import styled from "styled-components";
import Image from "@ingka/image";
import AspectRatioBox from "@ingka/aspect-ratio-box";

import { MarketsData } from "../../hooks/useGetMarkets";
import { HomePageLayoutTabs } from "../../utils/constants";
import { marketsByName } from "../../utils/common";
import { getImage } from "../../utils/markets";
import { space100, space200 } from "@ingka/variables/design-tokens";
import { PillDropdown } from "../Dropdown";

interface MarketLayoutProps {
	loading: boolean;
	setActiveMarket: (data: string) => void;
	activeMarket: string | null;
	activeTab: string | null;
	isAdmin: boolean;
	isAppAdmin: boolean;
	isFlaggaAdmin: boolean;
	setActiveTab: (value: string) => void;
	multipleMarketFlag: boolean;
	setMultipleMarketFlag: (multipleMarketUpdateFlag: boolean) => void;
	getCountryName: (country: string) => string;
	marketsData?: MarketsData;
}

const MarketTitleWrapper = styled.span`
	align-items: center;
	display: flex;
	gap: ${space100};
`;

const FlagImgIcon = styled.span`
	width: ${space200};
	height: ${space200};
`;

const Layout: React.FC<MarketLayoutProps> = ({
	loading,
	setActiveMarket,
	activeMarket,
	marketsData,
	activeTab,
	isAdmin,
	isAppAdmin,
	isFlaggaAdmin,
	setActiveTab,
	multipleMarketFlag,
	getCountryName,
}) => {
	if (!loading && !marketsData?.permittedMarkets && !marketsData?.otherMarkets) {
		return <Button text="Oops! No Markets" type="danger" size="small" disabled />;
	}

	const isDropdownDisabled =
		isAdmin && activeTab !== HomePageLayoutTabs.Configuration && activeTab !== HomePageLayoutTabs.MarketDashboard;

	const handleMarketChange = (value: string) => {
		setActiveMarket(value);

		if (isFlaggaAdmin || isAppAdmin) {
			setActiveTab(HomePageLayoutTabs.Configuration);
		}
	};

	const renderMarketItem = (market: any) => (
		<MenuItem
			id={market.code}
			key={market.code}
			title={
				<MarketTitleWrapper>
					<FlagImgIcon>
						<AspectRatioBox ratio="square">
							<Image src={getImage(market.code)} />
						</AspectRatioBox>
					</FlagImgIcon>

					<span>{`${market.name}${market.name === market.code ? "" : ` (${market.code})`}`}</span>
				</MarketTitleWrapper>
			}
		/>
	);

	const renderMarketList = (markets: string[], groupId?: string, groupName?: string) => {
		if (!markets.length) return null;

		const items = marketsByName(markets, getCountryName).map(renderMarketItem);

		if (!isAdmin && groupId && groupName) {
			return (
				<ListBoxGroup id={groupId} groupName={groupName}>
					{items}
				</ListBoxGroup>
			);
		}

		return items;
	};

	const renderMarketGroups = () => {
		const permitted = marketsData?.permittedMarkets || [];
		const other = marketsData?.otherMarkets || [];

		return (
			<>
				{renderMarketList(permitted, "allowed-markets", "Allowed Markets")}
				{renderMarketList(other, "other-markets", "Other Markets")}
			</>
		);
	};

	return (
		<>
			{!multipleMarketFlag && (
				<PillDropdown
					ariaLabel="Markets"
					id="markets-dropdown"
					value={activeMarket || undefined}
					label={activeMarket ? getCountryName(activeMarket) : undefined}
					disabled={isDropdownDisabled}
					onChange={handleMarketChange}
				>
					{renderMarketGroups()}
				</PillDropdown>
			)}
		</>
	);
};

export default Layout;
